/**
 * Owns the canvas element: devicePixelRatio, fit-to-window scaling, and the
 * per-frame transform. Screens draw in logical units and never think about it.
 */

import { CANVAS_HEIGHT, CANVAS_WIDTH, palette } from './theme.ts';
import { Painter } from './painter.ts';

export class Surface {
  readonly ctx: CanvasRenderingContext2D;
  readonly painter: Painter;
  private scale = 1;
  private dpr = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.painter = new Painter(ctx);
    this.resize();
  }

  /** Fit the fixed-aspect layout into the window, honouring devicePixelRatio. */
  resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.scale = Math.min(window.innerWidth / CANVAS_WIDTH, window.innerHeight / CANVAS_HEIGHT);

    const cssW = CANVAS_WIDTH * this.scale;
    const cssH = CANVAS_HEIGHT * this.scale;
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.canvas.width = Math.round(cssW * this.dpr);
    this.canvas.height = Math.round(cssH * this.dpr);
  }

  /**
   * Map a viewport point to the logical grid the screens are drawn in, or
   * `null` when it falls outside the letterboxed canvas.
   */
  toLogical(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = (clientX - rect.left) / this.scale;
    const y = (clientY - rect.top) / this.scale;
    if (x < 0 || y < 0 || x > CANVAS_WIDTH || y > CANVAS_HEIGHT) return null;
    return { x, y };
  }

  /** Signal that the pointer is over something clickable. */
  setPointer(over: boolean): void {
    this.canvas.style.cursor = over ? 'pointer' : 'default';
  }

  /** Reset the transform and clear to the background. Call once per frame. */
  begin(): void {
    const k = this.scale * this.dpr;
    this.ctx.setTransform(k, 0, 0, k, 0, 0);
    this.ctx.imageSmoothingEnabled = false;
    this.ctx.fillStyle = palette().background;
    this.ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  }
}
